import type { PageParam, PageResult, PagedMetaEnvelope, PagedQueryArg } from './paged-types';
import { buildPagedQueryString } from './query-params';

/**
 * Cache-tag identifiers for subscription endpoints. Co-located with the
 * query definitions so each endpoint's providesTags / invalidatesTags can
 * be authored next to the query it applies to (rather than centralized in
 * the API slice). The `tagTypes` array on the slice still has to register
 * `SUBSCRIPTION_TAG` for these to take effect.
 */
export const SUBSCRIPTION_TAG = 'Subscription' as const;
export const SUBSCRIPTION_LIST_ID = 'LIST' as const;

export type Addon = {
    id: string;
    name: string;
    quantity: number;
    type: string;
    productLevel: string;
    salesforceQuoteNumber: string;
    notes: string;
    startDate: string;
    finishDate: string;
    state: string;
};

type AddonApiItem = Partial<Addon>;

const hasAddonId = (a: AddonApiItem): a is AddonApiItem & { id: string } =>
    typeof a.id === 'string';

const transformAddon = (a: AddonApiItem & { id: string }): Addon => ({
    id: a.id,
    name: a.name ?? '',
    quantity: a.quantity ?? 0,
    type: a.type ?? '',
    productLevel: a.productLevel ?? '',
    salesforceQuoteNumber: a.salesforceQuoteNumber ?? '',
    notes: a.notes ?? '',
    startDate: a.startDate ?? '',
    finishDate: a.finishDate ?? '',
    state: a.state ?? 'I',
});

export type Subscription = {
    id: string;
    subscriptionName: string;
    customerName: string;
    companyName: string;
    salesforceLink: string;
    comment: string;
    state: string;
    tenantId: string;
    capacityGroupId: string;
    capacityGroupName: string;
    addons: Addon[];
};

export type SubscriptionApiItem = Partial<Omit<Subscription, 'addons'>> & {
    addons?: AddonApiItem[];
};

type SubscriptionsApiResponse = {
    subscriptions?: SubscriptionApiItem[];
    meta?: PagedMetaEnvelope;
};

type SubscriptionApiItemWithId = SubscriptionApiItem & { id: string };

const hasId = (sub: SubscriptionApiItem): sub is SubscriptionApiItemWithId =>
    typeof sub.id === 'string';

export const transformSubscription = (sub: SubscriptionApiItemWithId): Subscription => ({
    id: sub.id,
    subscriptionName: sub.subscriptionName ?? '',
    customerName: sub.customerName ?? '',
    companyName: sub.companyName ?? '',
    salesforceLink: sub.salesforceLink ?? '',
    comment: sub.comment ?? '',
    state: sub.state ?? 'I',
    tenantId: sub.tenantId ?? '',
    capacityGroupId: sub.capacityGroupId ?? '',
    capacityGroupName: sub.capacityGroupName ?? '',
    addons: Array.isArray(sub.addons) ? sub.addons.filter(hasAddonId).map(transformAddon) : [],
});

export type SubscriptionsPageResult = PageResult<Subscription>;
export type SubscriptionsQueryArg = PagedQueryArg;

export const subscriptionsInfiniteQuery = {
    query: ({
        queryArg,
        pageParam,
    }: {
        queryArg: SubscriptionsQueryArg;
        pageParam: PageParam;
    }) => ({
        url: `/mp/1/subscriptions?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: SubscriptionsApiResponse): SubscriptionsPageResult => {
        const rows = (response?.subscriptions ?? []).filter(hasId).map(transformSubscription);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 0,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: SubscriptionsPageResult,
            _allPages: SubscriptionsPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
    providesTags: [{ type: SUBSCRIPTION_TAG, id: SUBSCRIPTION_LIST_ID }],
};

/**
 * Subscriptions list endpoint used only as a small lookup for joining
 * customerName onto the paginated users list. Pagination is intentionally not
 * applied here — the dataset is small. Will be removed when the backend
 * embeds subscription on user.
 */
export const subscriptionsLookupQuery = {
    query: () => ({
        url: '/mp/1/subscriptions',
        method: 'get',
    }),
    transformResponse: (response: SubscriptionsApiResponse): Subscription[] => {
        if (!response?.subscriptions) return [];
        return response.subscriptions.filter(hasId).map(transformSubscription);
    },
};

export type SubscriptionsLookupResponse = Subscription[];
